import { h } from "preact";

import { uppercaseFirst } from "./string";
import interleave from "./interleave";
import repeat from "./repeat";
import Font from "../model/font";
import { entriesOf } from "./object";
import formatter from "./formatter";

type FontAxis = "wght" | "wdth" | "slnt" | "ital" | "opsz";

const REGISTERED_AXES: Record<FontAxis, string> = Object.freeze({
    wght: "Weight",
    wdth: "Width",
    slnt: "Slant",
    ital: "Italic",
    opsz: "Optical sizing"
});

const AXIS_ORDER: Record<FontAxis, number> = Object.freeze({
    wght: 0,
    wdth: 1,
    slnt: 2,
    ital: 3,
    opsz: 4
});

function enumerateFontVariationSettings(fvs: Record<string, number>): [string, number][] {
    return entriesOf(fvs).sort(([axis1], [axis2]) => {
        // Display registered axis values first
        if (axis1 in REGISTERED_AXES && axis2 in REGISTERED_AXES) {
            return AXIS_ORDER[axis1 as FontAxis] - AXIS_ORDER[axis2 as FontAxis];
        }

        if (axis1 in REGISTERED_AXES && !(axis2 in REGISTERED_AXES)) {
            return -1;
        }

        if (!(axis1 in REGISTERED_AXES) && axis2 in REGISTERED_AXES) {
            return 1;
        }

        return axis1 < axis2 ? -1 : 1;
    });
}

/**
 * Extracts main info from given text style
 * Main info is a string containing:
 *     - font stretch
 *     - font family
 *     - font style
 *     - font size
 *     - font weight
 *
 * `fontSize` is included in main info but returned separately because it
 * is rendered differently in using places.
 * @param textStyle To extract main info from
 * @returns Extracted content and fontSize to be appended
 */
function getMainInfo(textStyle: Font): {
    content: string;
    fontSize: number;
} {
    const {
        fontFamily,
        fontStyle,
        fontSize,
        stretchText,
        weightText,
        fontVariationSettings
    } = textStyle;

    const stretch = !fontVariationSettings && stretchText !== "normal" ? ` ${uppercaseFirst(stretchText)}` : "";
    const style = !fontVariationSettings && fontStyle !== "normal" ? ` ${uppercaseFirst(fontStyle)}` : "";
    const weight = !fontVariationSettings ? uppercaseFirst(weightText) : "";
    const separator = weight || style ? ", " : "";
    const content = `${fontFamily}${stretch}${separator}${weight}${style}`;

    return {
        content,
        fontSize
    };
}

/**
 * Extracts extra info from given text style
 * Extra info is a string containing:
 *     - text align
 *     - line height
 *     - letter spacing
 *     - font variation settings axes
 *
 * @param textStyle To extract extra info from
 * @param densityScale to format lineHeight and letterSpacing
 * @param textDimensionSuffix to format lineHeight and letterSpacing
 *
 * @returns Extra info text
 */
function getExtraInfo(textStyle: Font, densityScale: number, textDimensionSuffix: string): (string | h.JSX.Element)[] {
    const {
        textAlign,
        lineHeight,
        letterSpacing,
        fontVariationSettings
    } = textStyle;

    let extraInfo: (string | h.JSX.Element)[] = [];

    if (textAlign && textAlign !== "left") {
        extraInfo.push(uppercaseFirst(textAlign));
    }

    if (fontVariationSettings) {
        const fvs = enumerateFontVariationSettings(fontVariationSettings).map(([axisName, value]) => (
            <span key={axisName}>
                {REGISTERED_AXES[axisName as FontAxis] ?? <code>{axisName}</code>}: {formatter.fontVariationAxis(value)}
            </span>
        ));

        extraInfo.push(...fvs);
    }

    if (lineHeight) {
        extraInfo.push(`Line height: ${formatter.textLength(lineHeight, densityScale, textDimensionSuffix)}`);
    }

    if (typeof letterSpacing === "number") {
        extraInfo.push(`Letter spacing: ${formatter.letterSpacing(letterSpacing, densityScale, textDimensionSuffix)}`);
    }

    extraInfo = [...interleave(extraInfo, repeat(", "))];
    extraInfo.pop();

    return extraInfo;
}

export {
    REGISTERED_AXES,
    FontAxis,
    enumerateFontVariationSettings,
    getMainInfo,
    getExtraInfo
};
